import { defineStore } from "pinia";
import { shallowRef } from "vue";

import { dateRound, TimeUnit, PermissionException, DeferredPromise } from "@byloth/core";

import config from "@/config";

import Project, { ProjectPreview } from "@/models/project";
import useUserStore from "@/stores/user";
import { jsonStorage } from "@/utils";
import type { DateRange } from "@/types";

import * as Queries from "./queries";
import type { UiState } from "./types";

const _getDateRange = (): DateRange =>
{
    const dateStrings = jsonStorage.recall<[string, string]>("ui:internal:dateRange");
    if (!(dateStrings)) { return [new Date(Date.now() - (7 * TimeUnit.Day)), new Date()]; }

    return [new Date(dateStrings[0]), new Date(dateStrings[1])];
};

let _projectId = jsonStorage.read<number>("ui:internal:projectId");
let _projectPromise = new DeferredPromise<Project>();

export default defineStore("ui", {
    state: (): UiState => ({
        allProjects: shallowRef<ProjectPreview[]>([]),

        _currentProject: undefined,
        currentProject: shallowRef(_projectPromise),

        currentDateRange: _getDateRange()
    }),

    getters: {
        isHourly: (state): boolean =>
        {
            const [start, end] = state.currentDateRange;

            const startTime = start.getTime();
            const endTime = end.getTime();

            return ((endTime - startTime) <= TimeUnit.Day);
        },
        isDaily: (state): boolean =>
        {
            const [start, end] = state.currentDateRange;

            const startTime = start.getTime();
            const endTime = end.getTime();

            return ((endTime - startTime) <= (31 * TimeUnit.Day));
        },
        isWeekly: (state): boolean =>
        {
            const [start, end] = state.currentDateRange;

            const startTime = start.getTime();
            const endTime = end.getTime();

            return ((endTime - startTime) <= (52 * TimeUnit.Week));
        },
        isMonthly: (state): boolean =>
        {
            const [start, end] = state.currentDateRange;

            const startTime = start.getTime();
            const endTime = end.getTime();

            return ((endTime - startTime) <= (36 * TimeUnit.Month));
        }
    },
    actions: {
        _setProjectId(id?: number): void
        {
            _projectId = id;

            jsonStorage.write("ui:internal:projectId", id);
        },

        async _fetchAllProjects(): Promise<ProjectPreview[]>
        {
            const request = new Queries.AllProjects(useUserStore().token);
            const response = await request.execute();

            this.allProjects = response.map((project) => new ProjectPreview(project));

            return this.allProjects;
        },
        async _fetchProject(id: number): Promise<Project>
        {
            if (this._currentProject?.id === id) { return this._currentProject; }

            const request = new Queries.Project({ id }, useUserStore().token);
            const response = await request.execute();

            const project = new Project({
                ...response,

                coverImage: response.coverImage ? `${config.backendUrl}${response.coverImage}` : undefined
            });

            this._setProjectId(id);

            this._currentProject = project;
            _projectPromise.resolve(project);

            return project;
        },

        clear(): void
        {
            this._setProjectId();

            this.allProjects = [];

            this._currentProject = undefined;
            _projectPromise = new DeferredPromise<Project>();

            this.currentProject = _projectPromise;

            this.setDateRange();
        },
        async initialize(id?: number): Promise<Project>
        {
            await this._fetchAllProjects();

            if (!(this.allProjects.length))
            {
                throw new PermissionException(
                    "No projects found assigned to your account.\n" +
                    "Please, contact the support team for further assistance."
                );
            }

            return await this._fetchProject(id ?? _projectId ?? this.allProjects[0].id);
        },

        setDateRange(range?: DateRange): void
        {
            if (range)
            {
                const [start, end] = range;

                const startDate = dateRound(start);
                const endDate = dateRound(end);

                range = [startDate, endDate];

                jsonStorage.remember("ui:internal:dateRange", range);

                this.currentDateRange = range;
            }
            else
            {
                const startDate = dateRound(new Date(Date.now() - (7 * TimeUnit.Day)));
                const endDate = dateRound(new Date());

                jsonStorage.erase("ui:internal:dateRange");

                this.currentDateRange = [startDate, endDate];
            }
        }
    }
});
